import { RefObject, useEffect, useState } from "react";
import { Bounds, DragConfig, useGesture, Vector2 } from "@use-gesture/react";
import { dispatchGalleryMonetateQEvent } from "../../../Analytics/dispatchGalleryMonetateQEvent";

const initialDrag: Vector2 = [0, 0];

export const recalculateBoundaries = (
  imageRef: RefObject<HTMLImageElement>,
  containerRef: RefObject<HTMLDivElement>,
) => {
  const { width, height } = containerRef.current!.getBoundingClientRect();

  const { width: imageWidth, height: imageHeight } =
    imageRef.current!.getBoundingClientRect();
  if (imageWidth > width) {
    return {
      top: ((imageHeight - height) / 2) * -1,
      bottom: (imageHeight - height) / 2,
      left: ((imageWidth - width) / 2) * -1,
      right: (imageWidth - width) / 2,
    };
  }

  return {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  };
};

export const calculateScaleAfterDrag = (
  imageRef: RefObject<HTMLImageElement>,
  containerRef: RefObject<HTMLDivElement>,
): [number, number] => {
  const { bottom, left, right, top, width } =
    imageRef.current!.getBoundingClientRect();

  const { width: containerWidth, height: containerHeight } =
    containerRef.current!.getBoundingClientRect();

  if (width <= containerWidth) {
    return [0, 0];
  }

  let xOffset = 0,
    yOffset = 0;

  if (left > 0) {
    xOffset = -left;
  } else if (right < containerWidth) {
    xOffset = containerWidth - right;
  }

  if (top > 0) {
    yOffset = -top;
  } else if (bottom < containerHeight) {
    yOffset = containerHeight - bottom;
  }
  return [xOffset, yOffset];
};

export const useGalleryGesture = (
  containerRef: RefObject<HTMLDivElement>,
  imageRef: RefObject<HTMLImageElement>,
): [number, Vector2, () => void] => {
  const [scale, setScale] = useState(1);
  const [drag, setDrag] = useState<Vector2>(initialDrag);
  const [bounds, setBounds] = useState<Bounds>();

  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", handler);
    document.addEventListener("gesturechange", handler);
    document.addEventListener("gestureend", handler);
    return () => {
      document.removeEventListener("gesturestart", handler);
      document.removeEventListener("gesturechange", handler);
      document.removeEventListener("gestureend", handler);
    };
  }, []);

  const resetAdjustments = () => {
    setScale(1);
    setDrag(initialDrag);
    const bounds = recalculateBoundaries(imageRef, containerRef);
    setBounds(bounds);
  };

  useGesture(
    {
      onPinch: (state) => {
        const {
          offset: [d],
        } = state;

        setScale(d);
      },
      onPinchEnd: () => {
        dispatchGalleryMonetateQEvent("SuperZoom");
        setBounds(recalculateBoundaries(imageRef, containerRef));

        const [xOffset, yOffset] = calculateScaleAfterDrag(
          imageRef,
          containerRef,
        );
        setDrag([drag[0] + xOffset, drag[1] + yOffset]);
      },
      onDrag: ({ offset: [dx, dy], pinching, cancel }) => {
        if (pinching) {
          return cancel();
        }

        if (scale !== 1) {
          setDrag([dx, dy]);
        }
      },
    },
    {
      drag: {
        bounds,
        from: drag,
      } as DragConfig,
      pinch: {
        from: [scale, 0],
        scaleBounds: { min: 1, max: 5 },
      },
      target: containerRef,
    },
  );

  return [scale, drag, resetAdjustments];
};
